import { ChangeDetectorRef, Component, HostListener, NgZone, OnDestroy, OnInit } from '@angular/core';
import { DemoService } from 'src/app/services/common/demo.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { DemoFlowService } from 'src/app/services/common/demo-flow.service';

/** Un passo del giro: l'elemento da evidenziare (selettore CSS) e cosa dirne. */
interface PassoTour { selettore: string; titolo: string; testo: string; }

/** Un'espressione della mascotte: inclinazione del corpo e forma degli occhi. */
interface Espressione {
  nome: string;
  rot: number;                       // gradi di inclinazione del corpo
  sx: { w: number; h: number };      // occhio sinistro
  dx: { w: number; h: number };      // occhio destro
  spostaX: number;                   // scostamento orizzontale degli occhi
  spostaY: number;                   // scostamento verticale
}

/**
 * Giro guidato della demo pubblica, pagina per pagina: arrivando su mappa,
 * percorsi, allarmi o report parte il giro di quella pagina, che evidenzia i
 * suoi pezzi uno alla volta. Come i cartellini della demo su telefono, ma con
 * più passi perché sullo schermo grande c'è spazio per indicare gli elementi.
 * Un giro per pagina, una volta sola; dalla chat si può farlo ripartire.
 *
 * Non sono i ventinove passi della guida interna (StepGuideService): chi entra
 * da /demo non deve imparare la piattaforma, deve capire se gli serve. Di
 * quella guida però riusa i bersagli già marcati nelle pagine
 * (data-testid="step-guide-…"), accanto ai propri (data-tour="…").
 *
 * Vive nel guscio dell'applicazione, non dentro una pagina: deve poter
 * evidenziare anche i pulsanti della topbar, che sta in ion-header — un
 * contesto di impilamento a parte, sopra ion-content. Da dentro la pagina
 * l'anello finirebbe sotto la barra.
 *
 * I bersagli si cercano nel DOM per attributo `data-tour`, e si aspetta che
 * compaiano: la mappa e la barra si disegnano con tempi loro.
 */
@Component({
  selector: 'app-demo-tour',
  templateUrl: './demo-tour.component.html',
  styleUrls: ['./demo-tour.component.scss']
})
export class DemoTourComponent implements OnInit, OnDestroy {

  /** I giri, uno per pagina. L'ultimo passo di ognuno indica la pagina dopo. */
  readonly giri: {[pagina: string]: PassoTour[]} = {
    mappa: [
      { selettore: '[data-tour="cerca"]',                     titolo: 'Trova subito un veicolo',         testo: 'Scrivi targa o nome: anche con una flotta numerosa il mezzo lo trovi in un attimo.' },
      { selettore: '[data-tour="mappa-filtri"]',              titolo: 'Filtra con un clic',              testo: 'In movimento, fermi, senza segnale: guardi solo i mezzi che ti interessano adesso.' },
      { selettore: '[data-tour="mappa-lista"]',               titolo: 'Tutta la flotta, mezzo per mezzo', testo: 'Per ognuno vedi stato, velocità e da quanto è fermo. Cliccane uno per aprirne la scheda.' },
      { selettore: '[data-testid="step-guide-main-map"]',     titolo: 'La mappa in tempo reale',         testo: 'Le posizioni si aggiornano da sole: vedi dove sono i mezzi adesso, senza ricaricare la pagina.' },
      { selettore: '[data-tour="percorsi"]',                  titolo: 'E dove sono stati?',              testo: 'Da qui rivedi i viaggi di qualsiasi giorno: partenze, soste e chilometri.' }
    ],
    percorsi: [
      { selettore: '[data-tour="percorsi-veicolo"]',          titolo: 'Scegli il veicolo',               testo: 'Il mezzo di cui vuoi rivedere gli spostamenti.' },
      { selettore: '[data-tour="percorsi-date"]',             titolo: 'Scegli il periodo',               testo: 'Un giorno, una settimana o le date che vuoi: lo storico resta disponibile per mesi.' },
      { selettore: '[data-testid="step-guide-history-map"]',  titolo: 'Il percorso sulla mappa',         testo: 'Ogni tratto con le sue soste: dove si è fermato, a che ora e per quanto.' },
      { selettore: '[data-tour="allarmi"]',                   titolo: 'Vuoi saperlo mentre succede?',    testo: 'Con gli allarmi ti avvisiamo noi, senza dover controllare.' }
    ],
    allarmi: [
      { selettore: '[data-tour="allarmi-veicoli"]',           titolo: 'Parti da un veicolo',             testo: 'Scegli il mezzo a cui dare le regole: ogni veicolo ha le sue.' },
      { selettore: '[data-testid="step-guide-alarms-main"]',  titolo: 'Le regole che puoi impostare',    testo: 'Velocità, zone, movimento fuori orario, dispositivo scollegato, batteria scarica: appena succede ti arriva l\'avviso.' },
      { selettore: '[data-tour="report"]',                    titolo: 'I numeri della settimana',        testo: 'Nei report trovi chilometri, ore di guida e soste di ogni mezzo.' }
    ],
    report: [
      { selettore: '[data-testid="step-guide-report-select"]', titolo: 'Scegli i veicoli',               testo: 'Uno, alcuni o tutta la flotta insieme.' },
      { selettore: '[data-tour="report-periodo"]',            titolo: 'Scegli il periodo',               testo: 'Ieri, questa settimana o le date che vuoi.' },
      { selettore: '[data-testid="step-guide-report-request"]', titolo: 'Richiedi il report',            testo: 'Chilometri, ore di guida e soste, pronti da scaricare o da ricevere via email.' },
      { selettore: '[data-testid="topbar-demo-prova"]',       titolo: 'Tutto questo sui tuoi mezzi',     testo: 'Ti spediamo un localizzatore e lo provi gratis per un mese, senza impegno.' }
    ]
  };

  /** I passi del giro in corso: quelli della pagina in cui ci si trova. */
  passi: PassoTour[] = [];
  private pagina: string;

  /**
   * Una faccia per passo. Non è decorazione: la mascotte guarda verso quello
   * di cui si sta parlando e cambia espressione a ogni passo, così l'occhio
   * capisce che è successo qualcosa anche senza rileggere il testo.
   * Valori dal prototipo.
   */
  readonly espressioni: Espressione[] = [
    { nome: 'attentive-left', rot:  -6, sx: { w: 4,   h: 9   }, dx: { w: 4,   h: 9   }, spostaX: -1, spostaY:  0 },
    { nome: 'curious-left',   rot: -12, sx: { w: 4.5, h: 8   }, dx: { w: 4.5, h: 8   }, spostaX: -2, spostaY: -1 },
    { nome: 'surprised',      rot:   5, sx: { w: 6,   h: 10  }, dx: { w: 6,   h: 10  }, spostaX:  0, spostaY: -1 },
    { nome: 'joyful-wide',    rot:  -5, sx: { w: 5,   h: 4.5 }, dx: { w: 5,   h: 4.5 }, spostaX:  0, spostaY:  1 },
    { nome: 'playful-right',  rot:   9, sx: { w: 4,   h: 9   }, dx: { w: 4,   h: 5   }, spostaX:  1, spostaY:  0 }
  ];

  indice = 0;
  visibile = false;
  private fineDemo: Subscription;

  /** Il rilancio dopo il giro saltato: compare una volta sola. */
  rilancioVisibile = false;
  private static rilancioGiaMostrato = false;
  ancorato = false;   // finché non trova il bersaglio non disegna l'anello

  anello  = { left: 0, top: 0, width: 0, height: 0 };
  fumetto = { left: 0, top: 0 };

  private static readonly PADDING = 5;
  private static readonly LARGHEZZA_FUMETTO = 320;
  private static readonly TENTATIVI = 20;      // ~3 secondi in tutto
  private static readonly ATTESA = 150;

  /** Quanto si lascia esplorare prima di riproporre la prova a chi ha saltato
   *  il giro. Valore del prototipo, confermato. */
  private static readonly SECONDI_RILANCIO = 5;
  private timerRilancio: any;

  /** Le pagine il cui giro è già partito, in questa visita: tornandoci non
   *  riparte. Statico come i cartellini del telefono. */
  private static giriVisti: {[pagina: string]: boolean} = {};

  private timer: any;

  private sottoscrizione: Subscription;
  private navigazione: Subscription;

  constructor(
    private cd: ChangeDetectorRef,
    private zone: NgZone,
    private router: Router,
    private demoFlow: DemoFlowService
  ){}

  get passo(): PassoTour { return this.passi[this.indice]; }
  get espressione(): Espressione { return this.espressioni[this.indice % this.espressioni.length]; }

  /** La faccia contenta: occhi schiacciati, cioè il sorriso. La usa il
   *  rilancio, dove il tono è "bravo, stai andando bene". */
  get espressioneContenta(): Espressione {
    return this.espressioni.find(e => e.nome === 'joyful-wide') || this.espressioni[0];
  }

  /**
   * Il saltello di ingresso. Le animazioni CSS non ripartono se il nome non
   * cambia, quindi se ne alternano due identiche: a ogni passo il nome cambia
   * e il browser la riparte da capo.
   */
  get classeSaltello(): string { return this.indice % 2 ? 'pop-b' : 'pop-a'; }

  get ultimo(): boolean  { return this.indice === this.passi.length - 1; }
  get etichettaAvanti(): string { return this.ultimo ? 'Ho capito' : 'Avanti'; }

  ngOnInit(){
    // Se la demo finisce mentre il giro è in corso (registrazione dallo stepper
    // e rientro nel portale col proprio account) il giro si chiude da solo.
    this.fineDemo = DemoService.chiusa.subscribe(() => {
      clearTimeout(this.timerRilancio);
      this.rilancioVisibile = false;
      this.chiudi();
    });
    if(!DemoService.active) return;
    // Dalla chat si può chiedere di rifare il giro della pagina in cui si è:
    // lì il "già visto" non vale, l'ha chiesto l'utente.
    this.sottoscrizione = this.demoFlow.tourRichiesto$.subscribe(() => {
      this.rilancioVisibile = false;
      this.avviaGiro(DemoTourComponent.paginaDa(this.router.url), true);
    });
    // Ogni volta che si arriva su una pagina con il suo giro, parte quello.
    this.navigazione = this.router.events.pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: any) => this.avviaGiro(DemoTourComponent.paginaDa(e.urlAfterRedirects || e.url)));
    this.avviaGiro(DemoTourComponent.paginaDa(this.router.url));
  }

  /**
   * Fa partire il giro di una pagina. Cambiando pagina a metà giro, quello
   * vecchio si chiude: parlerebbe di cose che non ci sono più.
   */
  private avviaGiro(pagina: string, ancheSeVisto: boolean = false){
    // Solo nella demo pubblica: se nel frattempo la demo è finita (registrazione
    // e rientro con l'account) i cambi di pagina non fanno partire più niente.
    if(!DemoService.active){
      if(this.visibile) this.chiudi();
      return;
    }
    // La stessa navigazione può annunciarsi più volte: se il giro di questa
    // pagina è già in corso lo si lascia andare avanti.
    if(pagina && pagina === this.pagina && this.visibile && !ancheSeVisto) return;
    if(this.visibile) this.chiudi();
    if(!pagina || !this.giri[pagina]) return;
    if(DemoTourComponent.giriVisti[pagina] && !ancheSeVisto) return;
    DemoTourComponent.giriVisti[pagina] = true;
    this.rilancioVisibile = false;   // durante il giro niente altro a schermo
    this.pagina = pagina;
    this.passi = this.giri[pagina];
    this.vaiAlPasso(0);
  }

  /** Dall'indirizzo alla pagina che ha un giro: '/routes-history?x=1' → 'percorsi'. */
  private static paginaDa(url: string): string {
    const primo: string = (url || '').split('?')[0].split('/')[1] || '';
    return ({ 'map': 'mappa', 'routes-history': 'percorsi', 'alarms': 'allarmi', 'report': 'report' } as any)[primo] || null;
  }

  ngOnDestroy(){
    clearTimeout(this.timer);
    clearTimeout(this.timerRilancio);
    this.fineDemo?.unsubscribe();
    this.navigazione?.unsubscribe();
    this.sottoscrizione?.unsubscribe();
  }

  @HostListener('window:resize')
  onResize(){ if(this.visibile) this.misura(); }

  /** Si sposta su un passo e aspetta che il suo bersaglio esista. */
  private vaiAlPasso(i: number){
    clearTimeout(this.timer);
    this.indice = i;
    this.ancorato = false;
    this.attendiBersaglio(DemoTourComponent.TENTATIVI);
  }

  /**
   * Cerca il bersaglio a intervalli. Se dopo i tentativi non c'è — pagina
   * diversa, pulsante nascosto per quella piattaforma — il passo si salta
   * invece di lasciare l'anello sul bersaglio precedente, che mostrerebbe
   * un testo che parla di un'altra cosa.
   */
  private attendiBersaglio(rimasti: number){
    const el = this.bersaglio();
    if(el){
      this.visibile = true;
      this.misura(el);
      return;
    }
    if(rimasti <= 0){
      this.avanti();
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.timer = setTimeout(() => this.zone.run(() => this.attendiBersaglio(rimasti - 1)), DemoTourComponent.ATTESA);
    });
  }

  private bersaglio(): HTMLElement {
    const el = document.querySelector(this.passo.selettore) as HTMLElement;
    if(!el) return null;
    const r = el.getBoundingClientRect();
    // Presente ma di dimensione zero: c'è nel DOM e non è ancora disegnato.
    return (r.width > 0 && r.height > 0) ? el : null;
  }

  /** Posiziona anello e fumetto sull'elemento del passo corrente. */
  misura(elemento?: HTMLElement){
    const el = elemento || this.bersaglio();
    if(!el) return;
    const r = el.getBoundingClientRect();
    const p = DemoTourComponent.PADDING;
    this.anello = { left: r.left - p, top: r.top - p, width: r.width + p * 2, height: r.height + p * 2 };

    // Il fumetto sta sotto l'anello, ma senza uscire dalla finestra: sui
    // bersagli in fondo alla barra sborderebbe a destra.
    const sinistra = Math.max(12, Math.min(this.anello.left - 20,
                       window.innerWidth - DemoTourComponent.LARGHEZZA_FUMETTO - 12));
    let alto = this.anello.top + this.anello.height + 14;
    // Se non ci sta sotto, va sopra: meglio coprire l'anello che finire fuori
    // schermo con i pulsanti irraggiungibili.
    if(alto + 200 > window.innerHeight) alto = Math.max(12, this.anello.top - 200);
    this.fumetto = { left: sinistra, top: alto };

    this.ancorato = true;
    this.cd.detectChanges();
  }

  avanti(){
    // Finito il giro non si porta via nessuno: chi vuole la prova la chiede
    // dalla CTA in barra o dal rilancio. Arrivare in fondo a un tour e
    // ritrovarsi altrove è il modo più veloce per far chiudere la scheda.
    if(this.ultimo){ this.chiudi(); this.programmaRilancio(); return; }
    this.vaiAlPasso(this.indice + 1);
  }

  /**
   * Salta: si torna a esplorare. Niente proposta adesso — chi ha appena detto
   * "no grazie" non si riconvince con una finestra un secondo dopo — ma dopo
   * un po' di giro sulla mappa il rilancio arriva.
   */
  salta(){
    this.chiudi();
    this.programmaRilancio();
  }

  private programmaRilancio(){
    if(DemoTourComponent.rilancioGiaMostrato) return;
    clearTimeout(this.timerRilancio);
    this.zone.runOutsideAngular(() => {
      this.timerRilancio = setTimeout(() => this.zone.run(() => {
        if(DemoTourComponent.rilancioGiaMostrato) return;
        // Nel frattempo è partito il giro di un'altra pagina: il rilancio
        // aspetta che finisca, non ci si sovrappone.
        if(this.visibile) return this.programmaRilancio();
        DemoTourComponent.rilancioGiaMostrato = true;
        this.rilancioVisibile = true;
        this.cd.detectChanges();
      }), DemoTourComponent.SECONDI_RILANCIO * 1000);
    });
  }

  /** "Provalo sui tuoi veicoli" dal rilancio: porta alla richiesta. */
  accettaRilancio(){
    this.rilancioVisibile = false;
    this.cd.detectChanges();
    this.demoFlow.vaiAllaRichiestaProva();
  }

  /** "Non ora": si chiude e non si ripresenta più. */
  rifiutaRilancio(){
    this.rilancioVisibile = false;
    this.cd.detectChanges();
  }

  private chiudi(){
    clearTimeout(this.timer);
    this.visibile = false;
    this.ancorato = false;
    this.cd.detectChanges();
  }

}
